import { z } from 'zod';
import { COST_CATEGORIES, APPORTIONABLE } from '../enums';
import { zCents, zId } from './common';
import { partialOk } from './costs';

export const ImportBody = z.object({ kind: z.enum(['units', 'tenancies']), propertyId: zId, csv: z.string().min(1).max(5_000_000), delimiter: z.string().max(1).default(';'), mapping: z.record(z.string(), z.string()) });
export const TaxExportBody = z.object({ year: z.number().int().min(2000).max(2100), format: z.enum(['anlage_v', 'datev']), propertyId: zId.optional() });

/**
 * One position of the WEG statement (Hausgeldabrechnung, ADR-0016). `totalCents` = WEG total of the position: the
 * flat's amount is computed from the MEA (`mea`), or taken as stated (`amountCents`, share by the WEG's own key).
 * Without `totalCents` only the flat's `amountCents` is known (simple import, no outside total on the statement).
 * `nonApportionableCents` (apportionable 'partial') is part of `totalCents` when given, else of `amountCents`.
 */
export const HoaItem = z
  .object({
    category: z.enum(COST_CATEGORIES),
    label: z.string().max(120).default(''),
    totalCents: zCents.min(0).optional(),
    amountCents: zCents.min(0).optional(),
    apportionable: z.enum(APPORTIONABLE).default('yes'),
    nonApportionableCents: zCents.min(0).optional(),
    /** § 35a labor: of the WEG total when `totalCents` is given (scaled to the flat by the engine), else the flat's (ADR-0023). */
    laborCents35a: zCents.min(0).optional(),
  })
  .refine((v) => v.totalCents !== undefined || v.amountCents !== undefined, { message: 'Betrag angeben', path: ['totalCents'] })
  .refine((v) => v.totalCents === undefined || v.amountCents === undefined || v.amountCents <= v.totalCents, { message: 'Der Anteil der Wohnung ist größer als der Gesamtbetrag', path: ['amountCents'] })
  .refine((v) => v.laborCents35a === undefined || v.laborCents35a <= (v.totalCents ?? v.amountCents ?? 0), { message: 'Der Lohnanteil ist größer als der Betrag', path: ['laborCents35a'] })
  .refine((v) => partialOk({ apportionable: v.apportionable, amountCents: v.totalCents ?? v.amountCents ?? 0, ...(v.nonApportionableCents !== undefined ? { nonApportionableCents: v.nonApportionableCents } : {}) }), {
    message: 'Teilweise umlagefähig: nicht umlagefähigen Teil angeben (mehr als 0, weniger als der Betrag)',
    path: ['nonApportionableCents'],
  });
export const HoaStatementBody = z
  .object({
    year: z.number().int().min(2000).max(2100),
    unitId: zId.optional(),
    /** Miteigentumsanteil of the flat and the WEG total (e.g. 85.5 / 1000); remembered on unit and property. */
    mea: z.object({ share: z.number().positive(), total: z.number().positive() }).refine((m) => m.share <= m.total, { message: 'Der Anteil ist größer als die Summe', path: ['share'] }).optional(),
    /** A second import of the same year and flat replaces the earlier one (deactivated, never deleted). */
    replace: z.boolean().default(false),
    items: z.array(HoaItem).min(1),
    /** The uploaded Hausgeldabrechnung these positions come from: linked to the items and marked confirmed (no second booking). */
    documentId: zId.optional(),
  })
  .refine((v) => !!v.mea || v.items.every((i) => i.totalCents === undefined || i.amountCents !== undefined), { message: 'Miteigentumsanteil angeben', path: ['mea'] })
  .refine((v) => !v.mea || !!v.unitId, { message: 'Wohnung angeben', path: ['unitId'] });

/** Undo of a Hausgeld import (`POST /properties/:id/hoa-statement/revert`): the year's positions become inactive. */
export const HoaRevertBody = z.object({ year: z.number().int().min(2000).max(2100), unitId: zId.optional() });
